import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClientLayout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Plus, 
  FileText, 
  Clock, 
  CheckCircle2, 
  ArrowRight, 
  Trash2,
  Edit3,
  Eye,
  Edit,
  Tag,
  Loader2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDashboard() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [reopeningId, setReopeningId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API_URL}/submissions`);
      setSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API_URL}/submissions/${deleteId}`);
      setSubmissions(submissions.filter(s => s.id !== deleteId));
      toast.success('Submission deleted');
    } catch (error) {
      toast.error('Failed to delete submission');
    } finally {
      setDeleteId(null);
    }
  };

  const completedCount = submissions.filter(s => s.is_completed).length;
  const inProgressCount = submissions.filter(s => !s.is_completed).length;

  const handleReopenForEdit = async (submissionId) => {
    setReopeningId(submissionId);
    try {
      await axios.post(`${API_URL}/submissions/${submissionId}/edit`);
      toast.success('Submission reopened for editing');
      navigate(`/submissions/${submissionId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen submission');
    } finally {
      setReopeningId(null);
    }
  };

  return (
    <ClientLayout>
      <div className="page-container py-8">
        <PageHeader
          title="My Dashboard"
          description="Manage your compliance documents and submissions."
          actions={
            <Link to="/templates">
              <Button className="btn-primary px-4 py-2" data-testid="new-submission-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Submission
              </Button>
            </Link>
          }
        />

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="stat-card" data-testid="stat-total">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-[#FF6B35]" />
              </div>
              <div>
                <div className="stat-value">{submissions.length}</div>
                <div className="stat-label">Total Submissions</div>
              </div>
            </div>
          </div>

          <div className="stat-card" data-testid="stat-progress">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <div className="stat-value">{inProgressCount}</div>
                <div className="stat-label">In Progress</div>
              </div>
            </div>
          </div>

          <div className="stat-card" data-testid="stat-completed">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <div className="stat-value">{completedCount}</div>
                <div className="stat-label">Completed</div>
              </div>
            </div>
          </div>
        </div>

        {/* Submissions List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Submissions
            </h2>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="w-10 h-10 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-500">Loading submissions...</p>
            </div>
          ) : submissions.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No submissions yet</h3>
              <p className="text-slate-500 mb-6">Start by selecting a template and filling out your information.</p>
              <Link to="/templates">
                <Button className="btn-primary" data-testid="empty-new-submission-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Submission
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {submissions.map((submission) => (
                <div 
                  key={submission.id} 
                  className="px-6 py-4 hover:bg-slate-50 transition-colors"
                  data-testid={`submission-row-${submission.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        submission.is_completed ? 'bg-emerald-100' : 'bg-amber-100'
                      }`}>
                        {submission.is_completed ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        ) : (
                          <Clock className="h-5 w-5 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-medium text-slate-900">{submission.template_name}</h3>
                        <p className="text-sm text-slate-500">
                          Last updated: {new Date(submission.updated_at).toLocaleDateString()}
                          {submission.version && (
                            <span className="ml-2 inline-flex items-center gap-1 text-xs font-mono text-slate-400">
                              <Tag className="h-3 w-3" />{submission.version}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className={submission.is_completed ? 'status-success' : 'status-pending'}>
                        {submission.is_completed ? 'Completed' : 'In Progress'}
                      </span>
                      
                      <div className="flex items-center gap-2">
                        {submission.is_completed ? (
                          <>
                            <Link to={`/submissions/${submission.id}/preview`}>
                              <Button variant="ghost" size="sm" data-testid={`view-submission-${submission.id}`}>
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            </Link>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleReopenForEdit(submission.id)}
                              disabled={reopeningId === submission.id}
                              data-testid={`edit-submission-${submission.id}`}
                            >
                              {reopeningId === submission.id 
                                ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                : <Edit className="h-4 w-4 mr-1" />
                              }
                              Edit
                            </Button>
                          </>
                        ) : (
                          <Link to={`/submissions/${submission.id}`}>
                            <Button variant="ghost" size="sm" data-testid={`continue-submission-${submission.id}`}>
                              <Edit3 className="h-4 w-4 mr-1" />
                              Continue
                            </Button>
                          </Link>
                        )}
                        
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteId(submission.id)}
                          data-testid={`delete-submission-${submission.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="ml-14">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-500">Progress</span>
                      <span className="font-medium text-[#FF6B35]">
                        {submission.is_completed ? '100' : Math.round((submission.current_section / (submission.total_sections || 7)) * 100)}%
                      </span>
                    </div>
                    <div className="progress-bar h-2">
                      <div 
                        className="progress-fill" 
                        style={{ 
                          width: submission.is_completed 
                            ? '100%' 
                            : `${Math.round((submission.current_section / (submission.total_sections || 7)) * 100)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Submission?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All data entered in this submission will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ClientLayout>
  );
}
